import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Surface } from '@americanexpress/dls-react';
import { useDateDispatch, useDateSelector } from '../../context/dateContext';
import {
  setDates,
  setFocusedField,
  setIsDoneBtnClicked,
} from '../../ducks/dateDuck';
import {
  getIsDoneBtnDisabled,
  getIsAbleToResetDates,
  getFooterMinNightsStayLabelValue,
  getIsDoneBtnClicked,
} from '../../selectors/dateSelectors';
import {
  getDefaultTime,
  getIsAbleToResetTimes,
} from '../../selectors/timeSelectors';
import styles from './CalendarFooter.scss';
import { useTimeDispatch, useTimeSelector } from '../../context/timeContext';
import { setEndTime, setStartTime } from '../../ducks/timeDuck';
import { START_DATE } from '../../constants/dateConstants';
import useResetIsDoneBtnClickedHook from '../../hooks/resetIsDoneBtnClickedHook';

const CalendarFooter = ({ onClose, isInline, className }) => {
  const intl = useIntl();

  const dateDispatch = useDateDispatch();
  const isAbleToResetDates = useDateSelector(getIsAbleToResetDates);
  const isDoneBtnDisabled = useDateSelector(getIsDoneBtnDisabled);
  const isDoneBtnClicked = useDateSelector(getIsDoneBtnClicked);

  const minNightsLabelValue = useDateSelector(getFooterMinNightsStayLabelValue);

  const timeDispatch = useTimeDispatch();
  const defaultTime = useTimeSelector(getDefaultTime);
  const isAbleToResetTime = useTimeSelector(getIsAbleToResetTimes);
  useResetIsDoneBtnClickedHook();

  const handleResetDates = () => {
    dateDispatch(setDates({ startDate: null, endDate: null }));
    dateDispatch(setFocusedField(START_DATE));
    timeDispatch(setStartTime(defaultTime));
    timeDispatch(setEndTime(defaultTime));
  };

  if (isInline && isDoneBtnClicked) {
    return null;
  }

  return (
    <Surface
      variant="foreground"
      className={clsx(
        styles.calendarFooter,
        isInline ? 'border-0 pad-1-l pad-1-r pad-1-b' : 'pad-1',
        className
      )}
      aria-hidden="true"
    >
      <div className={clsx(styles.minStay)}>
        {minNightsLabelValue && (
          <Fragment>
            <span className="heading-3">
              <FormattedMessage className="label-2" id="minimum_nights" />
            </span>
            <span className="body-1">
              <FormattedMessage id="nights" values={{ minNightsLabelValue }} />
            </span>
          </Fragment>
        )}
      </div>
      <div className="flex">
        {(isAbleToResetTime || isAbleToResetDates) && (
          <Button
            id="resetBtn"
            aria-label={intl.formatMessage({ id: 'aria.reset_dates' })}
            className="margin-1-r automation-date-picker-reset-btn"
            onClick={handleResetDates}
            tabIndex="0"
            variant="tertiary"
          >
            <FormattedMessage id="reset" />
          </Button>
        )}
        <Button
          id="doneBtn"
          aria-label={intl.formatMessage({ id: 'done' })}
          className={clsx('automation-date-picker-done-btn', styles.doneButton)}
          tabIndex="0"
          aria-disabled={isDoneBtnDisabled}
          onClick={
            isInline
              ? () => {
                onClose();
                dateDispatch(setIsDoneBtnClicked(true));
              }
              : onClose
          }
          variant="primary"
        >
          <FormattedMessage id="done" />
        </Button>
      </div>
    </Surface>
  );
};

CalendarFooter.propTypes = {
  onClose: PropTypes.func.isRequired,
  isInline: PropTypes.bool,
  className: PropTypes.string,
};

CalendarFooter.defaultProps = {
  isInline: false,
};

export default CalendarFooter;
